import * as React from 'react';
import * as JSZip from 'jszip';
import * as Dropzone from 'react-dropzone-component';
import * as DropHandler from 'html5-file-selector';
import * as classnames from 'classnames';
import {Icon} from '../util';

interface Props {
	dropzoneErrorMessage: string;
	onReceiveFile(file: File): void;
	setDeployStatus(status: ConfigTester.DeployStatus): void;
	setProcessFileError(errorMessage: string): void;
}

interface State {
	dragStatus: 'active' | 'inactive';
	isDeployEnabled: boolean;
}

function isZipFile(files: ConfigTester.ExtendedDropzoneFile[]) {
	return files.length === 1 && files[0].type === 'application/zip';
}

function isFolder(files: ConfigTester.ExtendedDropzoneFile[]) {
	return files.length > 1;
}

function formatFilePath(path: string) {
	return path[0] === '/' ? path.slice(1) : path;
}

function getDropzoneText(dropzoneErrorMessage: string) {
	if (dropzoneErrorMessage) {
		return <h4>
			<span className='is-red'>*</span>
			{dropzoneErrorMessage}
		</h4>;
	} else {
		return <h4>Drop config folder or .zip to deploy</h4>;
	}
}

function isDeployEnabled(dropzoneErrorMessage: string) {
	return !dropzoneErrorMessage;
}

const ZIP_OPTIONS: JSZip.JSZipGeneratorOptions = {
	type: 'blob',
	compression: 'DEFLATE',
	compressionOptions: {
		level: 5,
	},
	mimeType: 'application/zip',
};

const DROPZONE_CONFIGURATION: Dropzone.DropzoneJSConfiguration = {
	paramName: 'config',
	method: 'NONE',
	addRemoveLinks: false,
	previewsContainer: false,
	uploadMultiple: false,
	autoProcessQueue: false,
	ignoreHiddenFiles: true,
	clickable: false,
	dictDefaultMessage: 'Drop config folder or .zip to deploy',
	dictFallbackText: 'Your browser does not support drag and drop.',
};

export default class FileSelector extends React.Component<Props, State> {

	public state: State = {
		dragStatus: 'inactive',
		isDeployEnabled: !this.props.dropzoneErrorMessage,
	};

	private zipFileInput: HTMLInputElement;
	private jsZipInstance: JSZip;
	private dropzoneInstance: Dropzone.Dropzone;

	constructor(props: Props, context: any) {
		super(props, context);
		this.jsZipInstance = new JSZip();
		this.handleZipFileBrowseUpload = this.handleZipFileBrowseUpload.bind(this);
		this.handleZipFileInputRef = this.handleZipFileInputRef.bind(this);
		this.handleBrowseButtonClick = this.handleBrowseButtonClick.bind(this);
		this.handleDragStart = this.handleDragStart.bind(this);
		this.handleDragEnd = this.handleDragEnd.bind(this);
		this.handleDrop = this.handleDrop.bind(this);
		this.setDropzoneInstance = this.setDropzoneInstance.bind(this);
		this.getDropzoneEventHandlers = this.getDropzoneEventHandlers.bind(this);
	}

	public componentDidUpdate(prevProps: Props) {
		const prev = isDeployEnabled(prevProps.dropzoneErrorMessage);
		const current = isDeployEnabled(this.props.dropzoneErrorMessage);
		if (current !== prev) {
			this.setState({
				...this.state,
				isDeployEnabled: current,
			});
		}
		if (current) {
			this.dropzoneInstance.enable();
		} else {
			this.dropzoneInstance.disable();
		}
	}

	public render() {
		return <div className='filedrop-container'>
			<div>
				<div className='dropzone-container'>
					<Dropzone.default
						eventHandlers={this.getDropzoneEventHandlers()}
						djsConfig={DROPZONE_CONFIGURATION}
						config={{postUrl: `no-url`, dropzoneSelector: 'body'}}
					>
						<Icon
							name='cloud-upload'
							className={this.getDropzoneClass()}
						/>
						{getDropzoneText(this.props.dropzoneErrorMessage)}
					</Dropzone.default>
				</div>
			</div>
			<h4>or</h4>
			{this.renderBrowseUpload()}
		</div>;
	}

	private renderBrowseUpload() {
		return <div>
			<input
				ref={this.handleZipFileInputRef}
				type='file'
				accept='.zip'
				original-title='&nbsp;'
				onChange={this.handleZipFileBrowseUpload}
			/>
			{this.renderBrowseButton()}
		</div>;
	}

	private renderBrowseButton() {
		return <button disabled={!this.state.isDeployEnabled} onClick={this.handleBrowseButtonClick}>
			Browse .zip Files
		</button>;
	}

	private handleBrowseButtonClick(event: React.MouseEvent<any>) {
		this.zipFileInput.click();
	}

	private handleZipFileInputRef(zipFileInput: HTMLInputElement) {
		this.zipFileInput = zipFileInput;
	}

	private handleZipFileBrowseUpload(event: React.FormEvent<HTMLInputElement>) {
		const files = event.currentTarget.files;
		const file = files && files[0];
		if (file) {
			this.props.setDeployStatus('LOADING');
			this.props.onReceiveFile(file);
		}
		event.currentTarget.value = '';
	}

	private handleDragStart() {
		this.setState({dragStatus: 'active'});
	}

	private handleDragEnd() {
		this.setState({dragStatus: 'inactive'});
	}

	private handleDrop(event: React.DragEvent<HTMLDivElement>) {
		this.handleDragEnd();
		this.props.setDeployStatus('LOADING');
		DropHandler.getDroppedOrSelectedFiles(event)
			.then(files => this.storeConfiguration(files))
			.catch((error: Error) => {
				this.props.setProcessFileError(`Unable to process drop, please check the contents: ${error.message}`);
				this.props.setDeployStatus('DEPLOYMENT_FAILURE');
				this.resetDropzoneInstance();
			});
	}

	private resetZipInstance() {
		this.jsZipInstance = new JSZip();
	}

	private resetDropzoneInstance() {
		this.dropzoneInstance.removeAllFiles(true);
	}

	private storeConfiguration(files: ConfigTester.ExtendedDropzoneFile[]) {
		if (isZipFile(files)) {
			this.props.onReceiveFile(files[0].fileObject);
			this.resetDropzoneInstance();
		} else if (isFolder(files)) {
			this.zipUpFolder(files);
		} else {
			this.props.onReceiveFile(files[0].fileObject);
			this.resetDropzoneInstance();
		}
	}

	private zipUpFolder(files: ConfigTester.ExtendedDropzoneFile[]) {

		const zipPromises = files.map(file => {
			if (file.fullPath) {
				return this.jsZipInstance.file(formatFilePath(file.fullPath), file.fileObject);
			} else {
				return this.jsZipInstance.file('', file.fileObject);
			}
		});

		Promise.all(zipPromises)
			.then(filesAdded => {
				return this.jsZipInstance.generateAsync(ZIP_OPTIONS)
					.then((content: Blob) => {
						const zipFile = new File([content], 'config.zip', {type: 'application/zip'});
						this.resetZipInstance();
						this.resetDropzoneInstance();
						this.props.onReceiveFile(zipFile);
					});
			})
			.catch((error: Error) => {
				this.props.setProcessFileError(`Unable to zip up file, please check the contents: ${error.message}`);
				this.props.setDeployStatus('DEPLOYMENT_FAILURE');
				this.resetZipInstance();
				this.resetDropzoneInstance();
			});

	}

	private setDropzoneInstance(dropzone: any) {
		this.dropzoneInstance = dropzone;
	}

	private getDropzoneEventHandlers(): Dropzone.DropzoneJSEventHandlers {
		return {
			init: this.setDropzoneInstance,
			drop: [this.handleDrop],
			dragenter: this.handleDragStart,
			dragover: this.handleDragStart,
			dragleave: this.handleDragEnd,
		};
	}

	private getDropzoneClass() {
		const activeAttribute = this.state.dragStatus === 'active' ? 'active pulse-icon' : 'inactive';
		return classnames('upload-icon', 'fa-5x', activeAttribute);
	}

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./ts/components/config-tester/FileSelector.tsx